import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import {
  BimDocument,
  BimFile,
  DeliveryModeType,
  Order,
  OrderEntry,
  StorageKey,
  TrackingFormSubmitStatus,
  TrackingFormType,
} from '@boels/models';
import { removeHtmlTagsFromString } from '../utils';
import { LocalStorageService } from './storage/local-storage.service';
import { GtmService } from './gtm.service';
import { getPricingDetails } from '../utils/get-unit-price-for-seo.utils';

export enum CartEntryEvent {
  AddToCart = 'add_to_cart',
  RemoveFromCart = 'remove_from_cart',
  UpdateCart = 'update_entry',
}

@Injectable({
  providedIn: 'root',
})
export class EcommerceTrackingService {
  #gtmService = inject(GtmService);
  #localStorageService = inject(LocalStorageService);

  setCartEntryEvent(
    cartId: string,
    orderEntries: OrderEntry[],
    cartEntryEvent: CartEntryEvent,
    obsoleteEntries?: OrderEntry[],
  ): void {
    const items = this.mapOrderItems(orderEntries || []);
    const cartEvent = {
      event: cartEntryEvent,
      ecommerce: {
        cart_id: cartId,
        items,
      },
    };

    if (cartEntryEvent === CartEntryEvent.UpdateCart) {
      const entryOldState = 'old_product_state ';
      const entryNewState = 'new_product_state';

      if (obsoleteEntries) {
        const removalCarEvent = {
          event: entryOldState,
          ecommerce: {
            cart_id: cartId,
            items: this.mapOrderItems(obsoleteEntries),
          },
        };
        this.#gtmService.pushTag(removalCarEvent);
      }
      cartEvent.event = entryNewState as CartEntryEvent;
      cartEvent.ecommerce.items = items;
    }

    this.#gtmService.pushTag(cartEvent);
  }

  setShippingInfoEvent(deliveryMode: DeliveryModeType | undefined): void {
    const shippingInfoEvent = {
      event: 'add_shipping_info',
      shipping_tier: deliveryMode,
    };

    this.#gtmService.pushTag(shippingInfoEvent);
  }

  setBeginCheckoutEvent(
    orderEntries: OrderEntry[],
    startDate: string,
    endDate: string,
  ): void {
    const beginCheckoutEvent = {
      event: 'begin_checkout',
      ecommerce: {
        reservation_details: this.getReservationDetails(startDate, endDate),
        items: this.mapOrderItems(orderEntries),
      },
    };

    this.#gtmService.pushTag(beginCheckoutEvent);
  }

  setCheckoutOptionEvent(option: string): void {
    const checkoutOptionEvent = {
      event: 'checkout_option',
      context: {
        checkout: {
          step: 3,
          option,
        },
      },
    };

    this.#gtmService.pushTag(checkoutOptionEvent);
  }

  setPurchaseEvent(order: Order): void {
    const deliveryPointOfService =
      order.pickupOrderGroups?.[0]?.deliveryPointOfService;
    const affiliation = deliveryPointOfService
      ? `${deliveryPointOfService?.name} - ${deliveryPointOfService?.displayName}`
      : '';

    const checkoutOptionEvent = {
      event: 'purchase',
      ecommerce: {
        transaction_id: order.code,
        affiliation,
        currency: this.#localStorageService.getItem(StorageKey.CURRENCY) || '',
        shipping_tier: order.deliveryMode?.code,
        newsletter_consent: order.newsletterConsent,
        newsletter_consent_type: order.newsletterConsentType,
        reservation_details: this.getReservationDetails(
          order.startDate || '',
          order.endDate || '',
        ),
        items: this.mapOrderItems(order.entries || []),
      },
    };

    this.#gtmService.pushTag(checkoutOptionEvent);
  }

  setProductSearchEvent(searchTerm: string, searchResultsCount: number): void {
    const searchEvent = {
      event: 'search',
      search_term: searchTerm,
      count_search_results: searchResultsCount,
    };

    this.#gtmService.pushTag(searchEvent);
  }

  setStoreLocatorSearchEvent(searchTerm: string): void {
    const searchEvent = {
      event: 'store_locator_search',
      search_term: searchTerm,
    };

    this.#gtmService.pushTag(searchEvent);
  }

  setFormSubmitEvent(
    formType: TrackingFormType,
    submitStatus: TrackingFormSubmitStatus,
  ): void {
    const formSubmitEvent = {
      event: 'form_submit',
      form_type: formType,
      submit_status: submitStatus,
      ...(formType === TrackingFormType.QUOTATION && {
        specialism: 'site_accommodation',
      }),
    };

    this.#gtmService.pushTag(formSubmitEvent);
  }

  setBimModelFileDownloadEvent(file: BimFile, bimDocument: BimDocument): void {
    const bimFileDownloadEvent = {
      event: 'bim_model_download',
      file_type: file.extension,
      model_brand: bimDocument.brand,
      model_type: bimDocument.brandType,
    };

    this.#gtmService.pushTag(bimFileDownloadEvent);
  }

  mapOrderItems(orderEntries: OrderEntry[]) {
    const items = orderEntries.map((item) => {
      const itemCategories = item.product?.alternates?.[0]?.nativeBreadcrumbs;
      const { price, currency, pricing_unit } = getPricingDetails(
        item.product?.unitPrices || [],
      );

      return {
        item_name: removeHtmlTagsFromString(item.product?.name || ''),
        item_id: item.product?.code,
        item_category: itemCategories?.[itemCategories.length - 2] || '',
        item_category2: itemCategories?.[itemCategories.length - 1] || '',
        quantity: item.quantity,
        is_accessory: item.isAccessory || false,
        price: price,
        currency: currency,
        pricing_unit: pricing_unit,
      };
    });
    return items;
  }

  getReservationDetails(startDate: string, endDate: string) {
    const datePipe = new DatePipe('en-US');
    return {
      start_date: datePipe.transform(startDate, 'dd-MM-yyyy'),
      end_date: datePipe.transform(endDate, 'dd-MM-yyyy'),
      days_until_start: this.calculateDurationInDays(startDate),
      duration: this.calculateDurationInDays(endDate, startDate),
    };
  }

  calculateDurationInDays(endDate: string, startDate?: string) {
    const endDateParsed = new Date(endDate).getTime();
    const startDateParsed = startDate
      ? new Date(startDate).getTime()
      : new Date().getTime();

    const difference = endDateParsed - startDateParsed;
    return Math.trunc(difference / (1000 * 60 * 60 * 24)) + 1;
  }
}
